/**
 * « Son visage » (V2Visage, docs/design/APP-APPLE.md §4.4): the character lives in a big preview (it follows the
 * pointer), its name under it; four bodies and seven colors to pick from, « Surprends-moi » (a random body and color,
 * and a jump of joy), « Garder <nom> ». Saved in AgentSettings.character (PATCH /api/settings, with the name when it
 * changed, and the matching accent for its cursor on the live screen); « Revenir à son personnage d'origine » clears it
 * (the default face). Shown while there is no avatar image, and kept for when the drawn face comes back.
 */
import { useEffect, useId, useRef, useState } from "react";
import type { AccentColor, AgentCharacter, AgentSettings } from "../../shared/contracts";
import { api, errorText } from "../lib/api";
import { invalidate, mutateResource } from "../lib/store";
import {
  CHARACTER_COLOR_HEX,
  CHARACTER_COLOR_LABEL,
  CHARACTER_COLOR_LIST,
  CHARACTER_SHAPE_LABEL,
  CHARACTER_SHAPE_LIST,
  lookOf,
  Perso,
  type CharacterColor,
  type CharacterLook,
  type CharacterMood,
} from "./avatar";
import { IconBack } from "./icons";
import { Link } from "@tanstack/react-router";
import { Button, cx, Notice, ROUND_BUTTON } from "./ui";

/** The agent's cursor on the live screen keeps the accent closest to its character's color. */
const ACCENT_OF: Record<CharacterColor, AccentColor> = {
  blanc: "graphite",
  mandarine: "violet",
  cerise: "rose",
  rose: "rose",
  bleuet: "bleu",
  menthe: "menthe",
  tournesol: "ambre",
};

function pickOne<T>(list: readonly T[], not?: T): T {
  const pool = list.length > 1 && not !== undefined ? list.filter((x) => x !== not) : list;
  return pool[Math.floor(Math.random() * pool.length)]!;
}

export function CharacterCreator({ settings }: { settings: AgentSettings }) {
  const savedName = settings.agentName;
  const saved = lookOf({ name: savedName, character: settings.character, main: true });
  const [look, setLook] = useState<CharacterLook>(saved);
  const [name, setName] = useState(savedName);
  const [mood, setMood] = useState<CharacterMood>("repos");
  const [busy, setBusy] = useState<"save" | "reset" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const touched = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nameId = useId();

  // A change made elsewhere (another tab, the agent itself) shows here until the owner starts composing.
  useEffect(() => {
    if (!touched.current) {
      setLook(lookOf({ name: savedName, character: settings.character, main: true }));
      setName(savedName);
    }
  }, [settings.character, savedName]);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  function cheer() {
    setMood("joie");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setMood("repos"), 1100);
  }

  function change(patch: Partial<CharacterLook>) {
    touched.current = true;
    setNotice(null);
    setLook((l) => ({ ...l, ...patch }));
    cheer();
  }

  const trimmed = name.trim();
  const nameChanged = trimmed !== savedName && trimmed.length > 0;
  const shown = trimmed || savedName;

  async function save(character: AgentCharacter | null) {
    if (busy) return;
    if (trimmed.length === 0 || trimmed.length > 40) {
      setError("Donne-lui un nom (40 caractères maximum).");
      return;
    }
    setBusy(character ? "save" : "reset");
    setError(null);
    setNotice(null);
    try {
      const color = character ? normalizeColor(character.color) : "blanc";
      const next = await api("PATCH /api/settings", {
        body: { character, accentColor: ACCENT_OF[color], ...(nameChanged ? { agentName: trimmed } : {}) },
      });
      mutateResource<AgentSettings>("settings", () => next);
      invalidate("dashboard");
      invalidate("bots");
      touched.current = false;
      setLook(lookOf({ name: next.agentName, character: next.character, main: true }));
      setName(next.agentName);
      setNotice(character ? `${next.agentName} garde ce visage.` : `${next.agentName} reprend son personnage d'origine.`);
      cheer();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  const changed = look.shape !== saved.shape || look.color !== saved.color || nameChanged;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex min-h-11 items-center gap-3">
        <Link to="/" aria-label="Retour" className={ROUND_BUTTON}>
          <IconBack width={16} height={16} strokeWidth={2.4} />
        </Link>
        <h1 className="min-w-0 flex-1 truncate font-display text-[28px] leading-tight font-medium">Son visage</h1>
        <Button
          variant="secondary"
          disabled={busy !== null}
          onClick={() => change({ shape: pickOne(CHARACTER_SHAPE_LIST, look.shape), color: pickOne(CHARACTER_COLOR_LIST, look.color) })}
          className="h-11 px-4 text-[14px] md:h-9"
        >
          Surprends-moi
        </Button>
      </header>

      {settings.avatar ? <Notice tone="neutral">Une image lui sert de visage. Ce personnage revient avec « Revenir au visage dessiné ».</Notice> : null}

      <div className="flex flex-wrap items-stretch gap-6">
        <section aria-label="Aperçu" className="flex min-h-[340px] min-w-0 flex-[1_1_320px] flex-col items-center justify-center gap-3.5 rounded-[28px] bg-elev px-4 py-6 md:min-h-[480px]">
          <Perso look={look} mood={mood} size={220} label={`${shown}, ton agent`} className="max-md:h-[187px] max-md:w-[170px]" />
          <label htmlFor={nameId} className="sr-only">
            Son nom
          </label>
          <input
            id={nameId}
            value={name}
            maxLength={40}
            onChange={(e) => {
              touched.current = true;
              setNotice(null);
              setName(e.target.value);
            }}
            className="h-11 w-[220px] max-w-full rounded-full bg-bg px-4 text-center font-display text-[24px] text-fg outline-none focus:ring-2 focus:ring-line-strong"
          />
        </section>

        <section aria-label="Choix" className="flex min-w-0 flex-[1_1_300px] flex-col gap-5 md:max-w-[340px] md:pt-1.5">
          <div role="group" aria-label="Forme" className="flex flex-col gap-2.5">
            <p aria-hidden="true" className="text-[13px] font-semibold text-subtle">
              Forme
            </p>
            <div className="grid grid-cols-4 gap-2">
              {CHARACTER_SHAPE_LIST.map((shape) => {
                const on = look.shape === shape;
                return (
                  <button
                    key={shape}
                    type="button"
                    aria-pressed={on}
                    aria-label={CHARACTER_SHAPE_LABEL[shape]}
                    onClick={() => change({ shape })}
                    className={cx(
                      "flex h-[76px] items-center justify-center rounded-[18px] bg-elev transition-transform duration-200 hover:-translate-y-0.5",
                      on && "shadow-[inset_0_0_0_2px_var(--fg)]",
                    )}
                  >
                    <Perso look={{ shape, color: look.color }} size={46} boop={false} />
                  </button>
                );
              })}
            </div>
          </div>

          <div role="group" aria-label="Couleur" className="flex flex-col gap-2.5">
            <p aria-hidden="true" className="text-[13px] font-semibold text-subtle">
              Couleur
            </p>
            <div className="flex flex-wrap gap-2 md:gap-2.5">
              {CHARACTER_COLOR_LIST.map((color) => {
                const on = look.color === color;
                return (
                  <button
                    key={color}
                    type="button"
                    aria-pressed={on}
                    aria-label={CHARACTER_COLOR_LABEL[color]}
                    onClick={() => change({ color })}
                    className="relative h-9 w-9 rounded-full after:absolute after:-inset-1 after:content-[''] max-md:h-11 max-md:w-11"
                    style={{ background: CHARACTER_COLOR_HEX[color], boxShadow: on ? "0 0 0 3px var(--bg), 0 0 0 5px var(--fg)" : undefined }}
                  />
                );
              })}
            </div>
          </div>

          <div className="flex-1" />
          <div className="flex flex-col gap-2">
            <Button size="lg" busy={busy === "save"} disabled={busy !== null || (!changed && settings.character !== null)} onClick={() => void save({ shape: look.shape, color: look.color })} className="h-[46px] w-full text-[15px]">
              Garder {shown}
            </Button>
            {settings.character ? (
              <Button variant="ghost" busy={busy === "reset"} disabled={busy !== null} onClick={() => void save(null)} className="self-center">
                Revenir à son personnage d'origine
              </Button>
            ) : null}
          </div>
        </section>
      </div>
      {notice ? (
        <p role="status" className="text-[13.5px] text-muted">
          {notice}
        </p>
      ) : null}
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
    </div>
  );
}

function normalizeColor(color: AgentCharacter["color"]): CharacterColor {
  return lookOf({ name: "", character: { shape: "rond", color } }).color;
}
